import type { Database } from 'sql.js';
import {
  rowFirstKeyColumn as col,
  sqlIntOrZero,
  sqlPositiveIntId,
  publicFileUrl,
  type SqlJsBind,
} from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import { ensureBonoIdForPuntoEntregaCaptura } from '@core/infrastructure/driven/sqljs/sqlJsBonoFkFromCaption.ts';
import {
  captureTipoBonoTextFromSqlRow,
  participanteTipoBonoCatalogId,
} from '@core/infrastructure/driven/sqljs/sqlJsLookupTipoBonoNombre.ts';
import type {
  RedencionBonoListItem,
  RedencionBonoParticipanteFormPayload,
} from '../../../domain/redencionBonoModel.ts';

/** Maps an unknown provider ID to a valid number or null. */
export function mapProveedorId(raw: unknown): number | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'string') {
    if (raw.trim() === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? raw : null;
  if (typeof raw === 'bigint') {
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  return null;
}

/** Ensures a voucher type ID is positive, or returns null. */
export function sqlPositiveBonoId(id: number | null | undefined): number | null {
  return id != null && id > 0 ? id : null;
}

/** Resolves persisted `bono_id` (required) and free-text `tipo_bono` for redemption lines. */
export function resolveRedencionParticipanteBonoFk(
  db: Database,
  line: RedencionBonoParticipanteFormPayload,
): { bonoId: number; tipoBono: string } {
  let bonoId = sqlPositiveBonoId(line.bonoId);
  const tipoBono = String(line.tipoBono ?? '').trim();
  const catalogHint = participanteTipoBonoCatalogId(db, line.participanteId) ?? 0;

  const caption = String(line.bono ?? '').trim();
  if (bonoId == null && caption.length > 0) {
    const ensured = ensureBonoIdForPuntoEntregaCaptura(db, caption, catalogHint);
    if (ensured != null && ensured.bonoId > 0) {
      bonoId = ensured.bonoId;
    }
  }

  if (bonoId == null) {
    throw new Error(`Participante ${line.participanteId}: indique el bono válido para la redención.`);
  }

  return { bonoId, tipoBono };
}

/** Converts an unknown SQL column value to a string safely. */
export function sqlStringCol(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'bigint' || typeof v === 'boolean') return String(v);
  return '';
}

/** Converts a database row into a redemption list item. */
export function rowToRedencionListItem(o: Record<string, unknown>): RedencionBonoListItem {
  const fechaRaw = sqlStringCol(o.fecha ?? o.FECHA).trim();
  const fecha = fechaRaw.length > 0 ? fechaRaw : new Date().toISOString().slice(0, 10);
  const offlineRaw = o.offline_uploaded_at ?? o.OFFLINE_UPLOADED_AT;
  const offlineStr = offlineRaw != null && offlineRaw !== '' ? String(offlineRaw).trim() : '';
  return {
    id: Number(o.id),
    publicId: sqlStringCol(o.public_id ?? o.PUBLIC_ID),
    entregaId: sqlIntOrZero(o.entrega_id ?? o.ENTREGA_ID),
    esRemanente: Number(o.es_remanente ?? o.ES_REMANENTE) === 1,
    fecha,
    proveedorId: mapProveedorId(o.proveedor_id ?? o.PROVEEDOR_ID),
    proveedorNombre: sqlStringCol(o.proveedor_nombre ?? o.PROVEEDOR_NOMBRE),
    responsableId: Number(o.responsable_id ?? o.RESPONSABLE_ID ?? 0),
    responsableNombre: sqlStringCol(o.responsable_nombre ?? o.RESPONSABLE_NOMBRE),
    latitud: sqlStringCol(o.latitud ?? o.LATITUD),
    longitud: sqlStringCol(o.longitud ?? o.LONGITUD),
    totalParticipantes: Math.max(0, Math.floor(Number(o.total_participantes ?? o.TOTAL_PARTICIPANTES ?? 0))),
    offlineUploadedAt: offlineStr.length > 0 ? offlineStr : null,
  };
}

/** Maps a database participant row to the form payload format. */
export function participanteRowToForm(
  db: Database,
  pep: Record<string, SqlJsBind>,
  filesPublicPrefix: string,
): RedencionBonoParticipanteFormPayload {
  return {
    participanteId: sqlIntOrZero(col(pep, 'participante_id', 'PARTICIPANTE_ID')),
    nombreParticipante: String(col(pep, 'nombre_participante', 'NOMBRE_PARTICIPANTE') ?? ''),
    telefono: String(col(pep, 'telefono', 'TELEFONO') ?? ''),
    celular: String(col(pep, 'celular', 'CELULAR') ?? ''),
    personaAutorizada: Number(col(pep, 'persona_autorizada', 'PERSONA_AUTORIZADA') ?? 0) === 1,
    nombrePersonaAutorizada: String(col(pep, 'nombre_persona_autorizada', 'NOMBRE_PERSONA_AUTORIZADA') ?? ''),
    asistencia: Number(col(pep, 'asistencia', 'ASISTENCIA') ?? 0) === 1,
    capacitado: Number(col(pep, 'capacitado', 'CAPACITADO') ?? 0) === 1,
    acta: publicFileUrl(col(pep, 'acta', 'ACTA') ?? '', filesPublicPrefix),
    entregaBolsa: Number(col(pep, 'entrega_bolsa', 'ENTREGA_BOLSA') ?? 0) === 1,
    bonoId: sqlPositiveIntId(col(pep, 'bono_id', 'BONO_ID')),
    bono: (() => {
      const joined = String(col(pep, 'bono_nombre', 'BONO_NOMBRE') ?? '').trim();
      return joined.length > 0 ? joined : String(col(pep, 'bono', 'BONO') ?? '');
    })(),
    tipoBono: captureTipoBonoTextFromSqlRow(db, pep),
    firmaBase64: publicFileUrl(col(pep, 'firma_base64', 'FIRMA_BASE64') ?? '', filesPublicPrefix),
    observaciones: String(col(pep, 'observaciones', 'OBSERVACIONES') ?? ''),
    registroFotografico: publicFileUrl(
      col(pep, 'registro_fotografico', 'REGISTRO_FOTOGRAFICO') ?? '',
      filesPublicPrefix,
    ),
    politicaTratamientos: Number(col(pep, 'politica_tratamientos', 'POLITICA_TRATAMIENTOS') ?? 0) === 1,
  };
}
